// KanbanBoard.js
import React, { forwardRef, useImperativeHandle } from 'react';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import {
  Box,
  Grid,
  HStack,
  Text,
  Icon,
  useColorModeValue,
  useToast,
  Heading,
  Button,
  Center,
  Spinner
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import KanbanColumn from './KanbanColumn';
import KanbanFilters from './KanbanFilters';
import NewLeadModalWrapper from './NewLeadModalWrapper';
import useKanbanBoardLogic from './KanbanBoardLogic';

const KanbanBoard = forwardRef(function KanbanBoard(props, ref) {
  const {
    stages,
    filteredLeads,
    isLoading,
    expandedStages,
    setExpandedStages,
    handleDragEnd,
    handleEditLead,
    handleAdvancedEditLead,
    handleAddNewLead,
    handleModalClose,
    selectedLead,
    fetchLeads,
    filterByMetric,
    searchQuery, setSearchQuery,
    callFilter, setCallFilter,
    tagFilter, setTagFilter,
    assigneeFilter, setAssigneeFilter,
    missedFollowUpFilter, setMissedFollowUpFilter,
    appliedDateFilter, setAppliedDateFilter,
    dateFilter, setDateFilter,
    datePreset, setDatePreset,
    focusedInput, setFocusedInput,
    users,
    loadMoreLeads,
    hasMore,
    loadingMore,
    isLoadingAllPages,
    setCallRangeMinMax
  } = useKanbanBoardLogic(); // Use the custom hook

    const bgColor = useColorModeValue('white', 'gray.700');
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    const columnBgColor = useColorModeValue('gray.50', 'gray.800');
    const columnHoverBgColor = useColorModeValue('gray.100', 'gray.700');


  useImperativeHandle(ref, () => ({
    refreshLeads: fetchLeads,
    filterByMetric: filterByMetric
  }));

  return (
    <Box py={1} bg={bgColor} minH="100vh">
      <KanbanFilters
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        callFilter={callFilter}
        setCallFilter={setCallFilter}
        tagFilter={tagFilter}
        setTagFilter={setTagFilter}
        assigneeFilter={assigneeFilter}
        setAssigneeFilter={setAssigneeFilter}
        missedFollowUpFilter={missedFollowUpFilter}
        setMissedFollowUpFilter={setMissedFollowUpFilter}
        appliedDateFilter={appliedDateFilter}
        setAppliedDateFilter={setAppliedDateFilter}
        dateFilter={dateFilter}
        setDateFilter={setDateFilter}
        datePreset={datePreset}
        setDatePreset={setDatePreset}
        focusedInput={focusedInput}
        setFocusedInput={setFocusedInput}
        users={users}
        handleAddNewLead={handleAddNewLead}
        onCallRangeChange={(min, max) => setCallRangeMinMax(min, max)}
      />

      {/* Loading Status and Indicators - Moved above the board */}
      {isLoadingAllPages && (
        <Center py={2} mb={2}>
          <Box textAlign="center" bg="blue.50" p={3} borderRadius="md" width="100%" maxW="600px">
            <Spinner size="md" color="blue.500" mb={2} />
            <Text fontWeight="medium">Loading all leads sequentially... Please wait.</Text>
          </Box>
        </Center>
      )}

      <DragDropContext onDragEnd={handleDragEnd}>
        <Grid templateColumns={stages.map(stage => {
            if ((stage._id === 'stage_closed_won' || stage._id === 'stage_closed_lost') && !expandedStages[stage._id]) {
              return '60px';
            }
            return '1fr';
          }).join(' ')} gap={3} maxW="100%" overflowX="auto" py={2}>
          {stages.map(stage => (
             <Box
             key={stage._id}
             bg={columnBgColor}
             p={2}
             borderRadius="lg"
             borderWidth="1px"
             borderColor={borderColor}
             minH={stage._id === 'stage_closed_won' || stage._id === 'stage_closed_lost' ? 'auto' : '500px'}
             boxShadow="sm"
             _hover={{
              bg: columnHoverBgColor
            }}
           >
             <Box
               onClick={() => {
                 if (stage._id === 'stage_closed_won' || stage._id === 'stage_closed_lost') {
                   setExpandedStages(prev => ({
                     ...prev,
                     [stage._id]: !prev[stage._id]
                   }));
                 }
               }}
               cursor={stage._id === 'stage_closed_won' || stage._id === 'stage_closed_lost' ? 'pointer' : 'default'}
               mb={4}
             >
                {(stage._id === 'stage_closed_won' || stage._id === 'stage_closed_lost') && !expandedStages[stage._id] ? (
                 <Box display="flex" flexDirection="column" alignItems="center">
                   <Box height="200px" display="flex" flexDirection="column" justifyContent="center" alignItems="center">
                     <Text
                       fontSize="md"
                       fontWeight="bold"
                       style={{ writingMode: 'vertical-lr', transform: 'rotate(180deg)' }}
                       mb={4}
                       noOfLines={2}
                     >
                       {stage.name}
                     </Text>
                     <Text
                       as="span"
                       color="gray.500"
                       mb={2}
                       style={{ writingMode: 'vertical-lr', transform: 'rotate(180deg)' }}
                     >
                       ({filteredLeads.filter(lead => lead &&
                         (typeof lead.stage === 'object' ? lead.stage?._id : lead.stage) === stage._id
                       ).length})
                     </Text>
                   </Box>
                   <Icon
                     as={ChevronLeftIcon}
                     w={6}
                     h={6}
                   />
                 </Box>
               ) : (
                 <HStack justify="space-between">
                   <Text fontSize="lg" fontWeight="bold">
                     {stage.name}{' '}
                     <Text as="span" color="gray.500">
                       ({filteredLeads.filter(lead => lead &&
                         (typeof lead.stage === 'object' ? lead.stage?._id : lead.stage) === stage._id
                       ).length})
                     </Text>
                   </Text>
                   {(stage._id === 'stage_closed_won' || stage._id === 'stage_closed_lost') && (
                     <Icon
                       as={ChevronRightIcon}
                       w={6}
                       h={6}
                     />
                   )}
                 </HStack>
               )}
             </Box>
             {(!['stage_closed_won', 'stage_closed_lost'].includes(stage._id) || expandedStages[stage._id]) && (
               <Droppable droppableId={stage._id}>
                 {(provided) => (
                   <Box
                     ref={provided.innerRef}
                     {...provided.droppableProps}
                     minH="100px"
                     flex="1"
                     h="100%"
                     display="flex"
                     flexDirection="column"
                   >
                      <KanbanColumn
                        stageId={stage._id}
                        title={stage.name}
                        leads={filteredLeads.filter(lead => lead &&
                         (typeof lead.stage === 'object' ? lead.stage?._id : lead.stage) === stage._id
                       )}
                       isLoading={isLoading}
                       onEdit={handleEditLead}
                       onAdvancedEdit={handleAdvancedEditLead}
                     />
                     {provided.placeholder}
                   </Box>
                 )}
               </Droppable>
             )}
           </Box>
          ))}
        </Grid>
      </DragDropContext>

      {/* Loading Status and Indicators - Bottom indicators */}
      <Center py={4} flexDirection="column">
        {!isLoadingAllPages && hasMore && !loadingMore && (
          <Button 
            colorScheme="blue" 
            onClick={loadMoreLeads} 
            isLoading={loadingMore}
            loadingText="Loading more leads..."
            variant="outline"
            size="md"
            width="200px"
          >
            Load More Leads
          </Button>
        )}
        
        {!isLoadingAllPages && !hasMore && !isLoading && filteredLeads.length > 0 && (
          <Text color="gray.500" fontSize="sm">
            All leads have been loaded
          </Text>
        )}
      </Center>

      {/* Loading indicator during initial load */}
      {isLoading && (
        <Center py={8}>
          <Spinner size="xl" thickness="4px" speed="0.65s" color="blue.500" />
        </Center>
      )}

      <NewLeadModalWrapper
        isOpen={!!selectedLead}
        onClose={handleModalClose}
        onLeadAdded={() => {}}
        lead={selectedLead}
      />
    </Box>
  );
});

KanbanBoard.displayName = 'KanbanBoard';
export default KanbanBoard;